// AcceptRequest.jsx - Generic component for accepting any request type via token
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SglContext from "../SglContext";


import './acceptRequest.css';
import GuardianConfirmation from './guardianConfirmation';

const AcceptRequest = () => {
  const Sgl = useContext(SglContext);
  const { token } = useParams();
  const navigate = useNavigate();
  const user_info_url = Sgl.nestApiUrl + '/users/getLoggedInUser';
  
  const [requestInfo, setRequestInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [userData, setUserData] = useState({});
  const [guardianName, setGuardianName] = useState('');
  const [guardianConfirmation, setGuardianConfirmation] = useState(null);

  // Map request types to user-friendly information
  const requestTypeConfig = {
    team_invitation: {
      title: "Team Invitation",
      subtitle: "Join a team and start collaborating",
      icon: "👥",
      successRedirect: "/edit-account/#team_management",
      declineRedirect: "/dashboard"
    },
    guardian_confirmation: {
      title: "Guardian Confirmation",
      subtitle: "Confirm your guardian relationship",
      icon: "👨‍👩‍👧‍👦",
      successRedirect: "/edit-account/#pending_requests",
      declineRedirect: "/dashboard"
    },
    friend_request: {
      title: "Friend Request", 
      subtitle: "Connect with another user",
      icon: "🤝",
      successRedirect: "/friends",
      declineRedirect: "/dashboard"
    },
    event_invitation: {
      title: "Event Invitation",
      subtitle: "Join an upcoming event",
      icon: "📅",
      successRedirect: "/events",
      declineRedirect: "/dashboard"
    }
  };

  function getUserData(){
        axios.get(user_info_url)
        .then((response) => {
          this.context.checkLoginOnSGL();
          if(response.data.user && response.data.user != null){
            setUserData(response.data.user)
            // this.setState({
            //   userData : response.data.user,
            // });
          }else{
          }
          window.onbeforeunload = "Are you sure you want to leave?";
          window.addEventListener('beforeunload', this.handleLeavePage);
        }, (error) => {
        });
      }

  useEffect(() => {
    if (token) {
      loadRequestInfo();
    } else {
      setError('Invalid request link');
      setLoading(false);
    }
  }, [token]);

  const loadRequestInfo = async () => {
    try {
      getUserData();
      setLoading(true);
      setError('');
      
      const response = await axios.get(`${Sgl.nestApiUrl}/user-requests/token/${token}`);
      setRequestInfo(response.data);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Invalid or expired request';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async () => {
    try {
      setAccepting(true);
      setError('');
      
      await axios.post(`${Sgl.nestApiUrl}/user-requests/accept-by-token/${token}`, {
        guardian_name: guardianName,
        agreement_id: requestInfo?.agreement_version?.agreement_id,
        version_id: requestInfo?.agreement_version?.sgl_id,
        customer_id: requestInfo.action_data?.customer_id,
        is_guardian: guardianConfirmation === 'yes' ? true : false,
      });
      
      setSuccess(true);
      
      let goToPath = window.referrer
      if(goToPath){
        navigate(goToPath);
      }else{
        // Redirect based on request type after a short delay
        const config = requestTypeConfig[requestInfo?.request_type] || requestTypeConfig.team_invitation;
        setTimeout(() => {
          navigate(config.successRedirect);
        }, 2500);
      }
      
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to accept request';
      setError(errorMessage);
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = async () => {
    try {
      await axios.post(`${Sgl.nestApiUrl}/user-requests/decline-by-token/${token}`);
      
      let goToPath = window.referrer
      if(goToPath){
        navigate(goToPath);
      }else{
        // Redirect based on request type
        const config = requestTypeConfig[requestInfo?.request_type] || requestTypeConfig.team_invitation;
        navigate(config.declineRedirect);
      }
      
    } catch (err) {
      console.error('Error declining request:', err);
      // Still navigate away even if decline fails
      navigate('/dashboard');
    }
  };

  const getRequestConfig = () => {
    return requestTypeConfig[requestInfo?.request_type] || requestTypeConfig.team_invitation;
  };

  const renderRequestSpecificInfo = () => {
    if (!requestInfo) return null;

    const actionData = requestInfo.action_data ? requestInfo.action_data : {};

    switch (requestInfo.request_type) {
      case 'team_invitation':
        return (
          <>
            <h2 className="primary-name">{actionData.team_name || 'Team'}</h2>
            {requestInfo.message && (
              <p className="primary-description">{requestInfo.message}</p>
            )}
            <div className="role-info">
              <span className="role-badge">{actionData.role_name || 'Member'}</span>
            </div>
          </>
        );

      case 'guardian_confirmation':
        return <GuardianConfirmation requestInfo={requestInfo} />

      case 'friend_request':
        return (
          <>
            <h2 className="primary-name">Friend Request</h2>
            <p className="primary-description">{requestInfo.message}</p>
          </>
        );

      case 'event_invitation':
        return (
          <>
            <h2 className="primary-name">{actionData.event_name || 'Event'}</h2>
            {requestInfo.message && (
              <p className="primary-description">{requestInfo.message}</p>
            )}
            <div className="role-info">
              <span className="role-badge">{actionData.role || 'Participant'}</span>
            </div>
          </>
        );

      default:
        return (
          <>
            <h2 className="primary-name">{requestInfo.title}</h2>
            {requestInfo.message && (
              <p className="primary-description">{requestInfo.message}</p>
            )}
          </>
        );
    }
  };

  if (loading) {
    return (
      <div className="accept-request-page">
        <div className="accept-request-container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <h2 className="loading-title">Loading request details...</h2>
            <p className="loading-message">Please wait while we verify your request</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    const config = getRequestConfig();
    const actionData = requestInfo?.action_data ? requestInfo.action_data : {};
    
    return (
      <div className="accept-request-page">
        <div className="accept-request-container">
          <div className="success-container">
            <div className="success-header">
              <div className="success-icon">✓</div>
              <h1 className="success-title">Request Accepted!</h1>
            </div>
            <div className="success-body">
              <p className="success-message">
                {requestInfo.request_type === 'team_invitation' && 
                  `You've successfully joined ${actionData.team_name} as ${actionData.role_name}.`
                }
                {requestInfo.request_type === 'guardian_confirmation' && 
                  `Guardian relationship confirmed successfully.`
                }
                {requestInfo.request_type === 'friend_request' && 
                  `Friend request accepted successfully.`
                }
                {requestInfo.request_type === 'event_invitation' && 
                  `You've successfully joined ${actionData.event_name}.`
                }
              </p>
              <div className="success-details">
                <div className="success-details-item">
                  <span className="success-details-label">Request Type</span>
                  <span className="success-details-value">{config.title}</span>
                </div>
                {requestInfo.sender_name && (
                  <div className="success-details-item">
                    <span className="success-details-label">From</span>
                    <span className="success-details-value">{requestInfo.sender_name}</span>
                  </div>
                )}
                <div className="success-details-item">
                  <span className="success-details-label">Accepted</span>
                  <span className="success-details-value">{new Date().toLocaleDateString()}</span>
                </div>
              </div>
              <p className="success-redirect-message">
                Redirecting you in a few seconds...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const config = getRequestConfig();
    
    return (
      <div className="accept-request-page">
        <div className="accept-request-container">
          <div className="error-container">
            <div className="error-header">
              <div className="error-icon">✕</div>
              <h1 className="error-title">Request Error</h1>
            </div>
            <div className="error-body">
              <p className="error-message">{error}</p>
              <div className="error-actions">
                <button className="modern-btn modern-btn-primary" onClick={loadRequestInfo}>
                  Try Again
                </button>
                <button className="modern-btn modern-btn-secondary" onClick={() => navigate('/dashboard')}>
                  Go to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const config = getRequestConfig();

  return (
    <div className="accept-request-page">
      <div className="accept-request-container">
        <div className="request-container">
          <div className="request-header">
            {/* <div className="request-icon">{config.icon}</div> */}
            <h1 className="request-title">{config.title}</h1>
            {/* <p className="request-subtitle">{config.subtitle}</p> */}
          </div>
          
          <div className="request-body">
            <div className="request-info-card">
              {renderRequestSpecificInfo()}
            </div>
            
            <div className="request-details">
              {requestInfo?.sender_name && (
                <div className="detail-item">
                  <div className="detail-label">From</div>
                  <div className="detail-value">{requestInfo.sender_name}</div>
                </div>
              )}
              <div className="detail-item">
                <div className="detail-label">Expires</div>
                <div className="detail-value">
                  {requestInfo?.expires_at 
                    ? new Date(requestInfo.expires_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })
                    : 'N/A'
                  }
                </div>
              </div>
            </div>

            {requestInfo?.request_type === 'guardian_confirmation' && (
              <>
                <div className="request-details name">
                  {/* == NEW: Guardian Name Input Field == */}
                  <div className="detail-item">
                    <div className="detail-label">Enter your name</div>
                    <div className="detail-value">
                    <input
                      id="guardianNameInput"
                      type="text"
                      className="detail-value-input" // You may need to add styling for this class
                      value={guardianName}
                      onChange={(e) => setGuardianName(e.target.value)}
                      placeholder="Enter your full name"
                    />
                    </div>
                  {/* </div>
                </div> */}
        
                {/* <div className="request-details name"> */}
                  {/* == NEW: Guardian Confirmation Radio Buttons == */}
                  <div className="guardian-confirmation-radios">
                    <div className="radio-option mt15">
                      <input
                        type="radio"
                        id="confirmYes"
                        name="guardianConfirm"
                        value="yes"
                        checked={guardianConfirmation === 'yes'}
                        onChange={(e) => setGuardianConfirmation(e.target.value)}
                      />
                      <label htmlFor="confirmYes">
                        Yes - I am the parent or legal guardian of {requestInfo?.action_data?.child_name}
                      </label>
                    </div>
                    <div className="radio-option">
                      <input
                        type="radio"
                        id="confirmNo"
                        name="guardianConfirm"
                        value="no"
                        checked={guardianConfirmation === 'no'}
                        onChange={(e) => setGuardianConfirmation(e.target.value)}
                      />
                      <label htmlFor="confirmNo">
                        No - I am not the parent or legal guardian of {requestInfo?.action_data?.child_name}
                      </label>
                    </div>
                  </div>
                  </div>
                </div>
              </>
            )}
            
            <div className="action-buttons">
              <button 
                className="modern-btn modern-btn-secondary"
                onClick={handleDecline}
                disabled={accepting || (requestInfo.request_type === 'guardian_confirmation' && guardianConfirmation !== 'no')}
              >
                Decline
              </button>
              <button 
                className="modern-btn modern-btn-primary"
                onClick={handleAcceptRequest}
                disabled={accepting || (requestInfo.request_type === 'guardian_confirmation' && (guardianConfirmation !== 'yes' || !guardianName))}
              >
                {accepting ? 'Accepting...' : 'Accept'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
AcceptRequest.contextType = SglContext;
export default AcceptRequest;